import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MusicNotes, ArrowLeft, Warning, ClockCounterClockwise } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="mb-1 block text-xs uppercase tracking-widest text-ff-brass">{label}</label>
    {children}
  </div>
);

export default function AdminAudio() {
  const [cfg, setCfg] = useState(null);
  const [defaultVolume, setDefaultVolume] = useState("");
  const [devLabel, setDevLabel] = useState("");
  const [disabledTracks, setDisabledTracks] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/audio/admin/config").then((r) => {
      setCfg(r.data);
      const ov = r.data.overrides || {};
      setDefaultVolume(ov.default_volume ?? "");
      setDevLabel(ov.development_audio_label ?? "");
      setDisabledTracks((ov.disabled_tracks || []).join(", "));
    }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!reason.trim()) { toast.error("A reason is required for audio config changes"); return; }
    setSaving(true);
    const payload = { reason: reason.trim() };
    if (defaultVolume !== "") payload.default_volume = parseFloat(defaultVolume);
    if (devLabel !== "") payload.development_audio_label = devLabel;
    if (disabledTracks.trim() !== "") payload.disabled_tracks = disabledTracks.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put("/audio/admin/config", payload);
      toast.success("Audio configuration saved");
      setReason("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const emergencyDisable = async () => {
    try { await api.post("/audio/admin/emergency-disable"); toast.success("Runtime audio disabled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!cfg) return <div className="py-20 text-center text-ff-muted">Loading audio configuration…</div>;

  const stateCount = Object.keys(cfg.states || {}).length;

  return (
    <div className="space-y-5" data-testid="admin-audio-page">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MusicNotes size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Expedition Pulse — Config</h1>
        </div>
        <Link to="/admin" data-testid="admin-audio-back" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs uppercase tracking-wider text-ff-brass hover:border-ff-gold hover:text-ff-gold"><ArrowLeft size={14} /> Admin</Link>
      </div>

      <section className="ff-panel rounded-md p-5" data-testid="admin-audio-flags">
        <h2 className="mb-2 text-lg font-semibold text-ff-gold">Feature Flags & Catalog</h2>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(cfg.flags || {}).map(([k, v]) => (
            <span key={k} data-testid={`admin-audio-flag-${k}`} className={`rounded-full border px-2.5 py-1 ${v ? "border-emerald-500/40 text-emerald-300" : "border-ff-brass/30 text-ff-muted"}`}>{k}: {String(v)}</span>
          ))}
        </div>
        <p className="mt-3 text-xs text-ff-muted">{stateCount} audio states defined · transition types: {(cfg.transition_types || []).join(", ")}</p>
      </section>

      <section className="ff-panel rounded-md p-5" data-testid="admin-audio-overrides">
        <h2 className="mb-1 text-lg font-semibold text-ff-gold">Configuration Overrides</h2>
        <p className="mb-3 flex items-center gap-1.5 text-xs text-ff-muted"><Warning size={14} className="text-ff-gold" /> Local allowlisted audio only — remote asset URLs are rejected by the server. Every change is audited.</p>
        <Field label="Default volume (0–1)">
          <input type="number" min="0" max="1" step="0.05" value={defaultVolume} onChange={(e) => setDefaultVolume(e.target.value)}
            data-testid="admin-audio-default-volume" className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment" placeholder="0.6" />
        </Field>
        <Field label="Development audio label">
          <input type="text" value={devLabel} onChange={(e) => setDevLabel(e.target.value)}
            data-testid="admin-audio-dev-label" className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment" placeholder="Development placeholder audio" />
        </Field>
        <Field label="Disabled tracks (comma-separated ids)">
          <input type="text" value={disabledTracks} onChange={(e) => setDisabledTracks(e.target.value)}
            data-testid="admin-audio-disabled-tracks" className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment" placeholder="motif.exp.gilded_meridian" />
        </Field>
        <Field label="Reason (required, audited)">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="admin-audio-reason" className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment" placeholder="Why are you changing this?" />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={save} disabled={saving} data-testid="admin-audio-save" className="rounded-sm bg-ff-gold px-4 py-2 text-sm font-semibold text-ff-bg hover:bg-ff-brass disabled:opacity-50">{saving ? "Saving…" : "Save configuration"}</button>
          <button onClick={emergencyDisable} data-testid="admin-audio-emergency" className="rounded-sm border border-red-500/50 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10">Emergency disable runtime audio</button>
        </div>
      </section>

      <section className="ff-panel rounded-md p-5" data-testid="admin-audio-history">
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-ff-gold"><ClockCounterClockwise size={18} weight="duotone" /> Version History</h2>
        {(cfg.version_history || []).length === 0 ? <p className="text-sm text-ff-muted">No changes recorded yet.</p> : (
          <div className="space-y-1.5">
            {cfg.version_history.map((h) => (
              <div key={h.id} className="flex items-center justify-between border-b border-ff-brass/10 py-1.5 text-sm" data-testid={`admin-audio-version-${h.version}`}>
                <span className="text-ff-parchment">v{h.version} · {h.reason}</span>
                <span className="text-xs text-ff-muted">{String(h.at).slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
